import React from 'react';
import {
    TextInput,
    NumberInput,
    DateInput,
    BooleanInput,
    SelectInput,
    required,
    useRecordContext,
    FormDataConsumer
} from 'react-admin';
import { Grid } from '@mui/material';
import { useMetadataDefinitions } from '../lib/customHooks';

const DynamicMetadataFields = ({ entityType, mode = 'edit', readOnly = false }) => {
    const record = useRecordContext();
    const metadataDefinitions = useMetadataDefinitions(entityType);
    const role = localStorage.getItem('role');
    
    // Filter definitions based on mode and permissions
    const visibleDefinitions = metadataDefinitions.filter(def => {
        if (mode === 'show') {
            return def.charity_can_view || role === 'admin' || role === 'umbrella';
        }
        return true;
    });
    
    if (!visibleDefinitions || visibleDefinitions.length === 0) {
        return null;
    }
    
    const renderField = (definition) => {
        // Use field_name directly as it comes in snake_case from the backend
        const fieldName = `metadata.${definition.field_name}`;
        const isEditable = definition.charity_can_edit || role === 'admin' || role === 'umbrella';
        const validationRules = definition.is_required ? [required()] : [];
        
        // Common props for all field types
        const commonProps = {
            source: fieldName,
            label: definition.field_label,
            validate: validationRules,
            disabled: readOnly || (mode === 'edit' && !isEditable),
            fullWidth: true,
            ...(definition.field_description && { helperText: definition.field_description })
        };
        
        switch (definition.field_type) {
            case 'TEXT':
                return <TextInput {...commonProps} />;
                
            case 'NUMBER':
                return <NumberInput {...commonProps} />;
                
            case 'DATE':
                return <DateInput {...commonProps} />;
                
            case 'BOOLEAN':
                return <BooleanInput {...commonProps} sx={{ '& .MuiFormControlLabel-label': { marginLeft: 1 } }} />;
                
            case 'SELECT':
                const choices = (definition.field_options || []).map(option => ({
                    id: option,
                    name: option
                }));
                return <SelectInput {...commonProps} choices={choices} />;
                
            default:
                return <TextInput {...commonProps} />;
        }
    };
    
    return (
        <FormDataConsumer>
            {({ formData, ...rest }) => {
                // Initialize metadata object if it doesn't exist
                if (!formData.metadata) {
                    formData.metadata = record?.metadata || {};
                }
                
                return (
                    <Grid container spacing={4} sx={{ padding: '8px' }} xs={12} lg={12}>
                        {visibleDefinitions.map((definition) => (
                            <Grid item xs={12} key={definition.id}>
                                {renderField(definition)}
                            </Grid>
                        ))}
                    </Grid>
                );
            }}
        </FormDataConsumer>
    );
};

export default DynamicMetadataFields;